import { Palette } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import colors from './base/colors';
import pxToRem from './functions/pxToRem';
import fontSizeResponsive from 'assets/theme/functions/fontSizeResponsive';


const size = { pageTitle: 18, sectionHeader: 18, subHeader: 18, body: 14, bodyValue: 18, label: 14 };
const weight = { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700 };

export const pepWiseTypography : TypographyOptions | ((palette: Palette) => TypographyOptions) = {
	fontFamily: '"Montserrat", "lato", "Helvetica", "Arial", "sans-serif"',

	// Use for page titles. Examples: "Mosaic Dashboard", "Resource Mapping Overview"
	pageHeader: { ...fontSizeResponsive(size.pageTitle), fontWeight: weight.medium, fontFamily: 'Montserrat', textTransform: 'uppercase', lineHeight: 1.3 },

	// Use for section headers within a page. Examples: "My Favorites", "Recent Activity"
	sectionHeader: { ...fontSizeResponsive(size.sectionHeader), fontWeight: weight.medium, fontFamily: 'Montserrat', lineHeight: 1.35 },

	// Use for subheaders. Examples: "Top Insights", "Alerts Summary"
	subHeader: { ...fontSizeResponsive(size.subHeader), fontWeight: weight.medium, fontFamily: 'Montserrat', lineHeight: 1.4 },

	// Use for all body copy, helper text in cards & grids.
	bodyText: { ...fontSizeResponsive(size.body), fontWeight: weight.medium, fontFamily: 'Montserrat', lineHeight: 1.55 },

	// Use for all body values in cards & grids.
	bodyValue: { ...fontSizeResponsive(size.bodyValue), fontWeight: weight.semibold, fontFamily: 'Montserrat', lineHeight: 1.55 },

	// Use for all body labels in cards & grids.
	bodyLabel: { ...fontSizeResponsive(size.bodyValue), fontWeight: weight.regular, fontFamily: 'Montserrat'},

	// Use for filters, inputs, and placeholder-like text. Examples: "Search modules…", "Filter by region"
	filterText: { ...fontSizeResponsive(size.body), fontWeight: weight.light, fontFamily: 'Montserrat', lineHeight: 1.55 },

	// new typo for dropdown filter text, inputs, and placeholder-like text. Examples: "Search modules…", "Filter by region"
	dropdownText: { ...fontSizeResponsive(size.body), fontWeight: weight.medium, fontFamily: 'Montserrat', lineHeight: 1.55 },

	// new typo for dropdown list item text.
	dropdownListText: { ...fontSizeResponsive(size.body), fontWeight: weight.regular, fontFamily: 'Montserrat', lineHeight: 1.55 },

	// Use for labels, chips, small tags, CTA, Button typos. Examples: "In Progress", "Critical"
	labelText: {
		...fontSizeResponsive(size.label),
		fontWeight: weight.semibold,
		fontFamily: 'Montserrat',
		lineHeight: 1.2,
		textTransform: 'uppercase',
	},

	// Use for Tab page header.
	tabPageHeader: {
		...fontSizeResponsive(18),
		fontWeight: weight.medium,
		fontFamily: 'Montserrat',
	},

	// Use for Tab labels.
	tabLabel: {
		...fontSizeResponsive(16),
		fontWeight: weight.medium,
		fontFamily: 'Montserrat',
	},

	// Used for Global sidebar main items
	sidebarMain: {
		...fontSizeResponsive(16),
		fontWeight: weight.regular,
		fontFamily: 'Montserrat',
	},

	// Used for Global sidebar child items
	sidebarChild: {
		...fontSizeResponsive(14),
		fontWeight: weight.regular,
		fontFamily: 'Montserrat',
	},

	// for landing page tile title
	dashboardTileTitle: {
		fontFamily: 'Montserrat',
		fontWeight: 500,
		fontSize: '18px',
		lineHeight: '30px',
		letterSpacing: 0,
		wordBreak: 'break-word',
	},
	// for landing page tile tagline/caption
	dashboardTileCaption: {
		display: 'block',
		fontFamily: 'Montserrat',
		fontStyle: 'normal',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		fontSize: '12px'
	},
	widgetTitleNew: {
		...fontSizeResponsive(18),
		fontWeight: 500,
		fontFamily: 'Montserrat',
		// restrict line breaks to 2
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		WebkitBoxAlign: 'start'
	},
	widgetValueNew: {
		...fontSizeResponsive(32),
		fontWeight: 400,
		fontFamily: 'Montserrat'
	},
	metricValue: {
		...fontSizeResponsive(48),
		fontWeight: 400,
		fontFamily: 'Montserrat'
	},
	widgetUnitNew: {
		...fontSizeResponsive(18),
		fontWeight: 400,
		marginLeft: '10px',
		fontFamily: 'Montserrat'
	},
	widgetRatioValueNew: {
		...fontSizeResponsive(32),
		fontWeight: 500,
		fontFamily: 'Montserrat'
	},
	mainTitle: {
		fontSize: pxToRem(48),
		fontWeight: 400,
		fontFamily: 'Montserrat'
	},
	subtitle1: {
		fontSize: pxToRem(12),
		fontWeight: 600
	},
	subtitle2: {
		fontSize: pxToRem(14),
		fontWeight: 500,
		fontStyle: 'italic'
	},
	navMenu: {
		fontSize: pxToRem(16),
		color: colors.white.main
	},
	pageTitle: {
		fontSize: pxToRem(20),
		fontWeight: 600
	},
	breadcrumbs: {
		...fontSizeResponsive(18),
		fontWeight: 500
	},
	widgetTitle: {
		...fontSizeResponsive(18),
		fontWeight: 500,
		fontFamily: 'Montserrat',
		// restrict line breaks to 2
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		WebkitBoxAlign: 'start'
	},
	widgetValue: {
		...fontSizeResponsive(50),
		fontWeight: 500,
		fontFamily: 'Montserrat'
	},
	widgetRatioValue: {
		...fontSizeResponsive(32),
		fontWeight: 600,
		fontFamily: 'Montserrat'
	},
	widgetUnit: {
		...fontSizeResponsive(28),
		fontWeight: 500,
		marginLeft: '10px',
		fontFamily: 'Montserrat'
	},
	overlayWidgetTitle: {
		...fontSizeResponsive(18),
		fontWeight: 500,
		lineHeight: '19.5px',
		fontFamily: 'Montserrat'
	},
	overlayWidgetValue: {
		...fontSizeResponsive(50),
		fontWeight: 500,
		lineHeight: '22.4px',
		fontFamily: 'Montserrat'
	},
	overlayWidgetUnit: {
		...fontSizeResponsive(28),
		fontWeight: 500,
		lineHeight: '14px',
		marginLeft: '10px',
		fontFamily: 'Montserrat'
	},
	// Use for all table cells.
	tableColumnFont: {
		...fontSizeResponsive(14),
		fontWeight: 500,
		fontFamily: 'Montserrat'
	},
	// Use for all table cells.
	tableRowFont: {
		...fontSizeResponsive(14),
		fontWeight: 500,
		fontFamily: 'Montserrat'
	},
	
	footerSubtitle: {
		fontSize: pxToRem(14),
		fontWeight: 500,
		fontStyle: 'italic'
	},

	widgetBodyLabel: {
		fontSize: pxToRem(14),
		fontWeight: 300,
	},

	widgetBodyLabelStrong: {
		fontSize: pxToRem(14),
		fontWeight: 600,
		textTransform: 'uppercase',
	},
};
